<template>
    <div>
        <v-card>
            <v-card-title ref="title">
                <v-btn
                    class="mr-3"
                    depressed
                    color="primary"
                    icon
                    rounded
                    small
                    @click="closeTimeLine()"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <h3>Purchase {{ request.number }}</h3>
            </v-card-title>
            <v-divider class="ml-4 mr-5"></v-divider>
            <v-card-text
                class="mt-4 overflow-y-auto"
                :style="{ 'max-height': `${height}px` }"
            >
                <div
                    v-if="!timelineItems.length"
                    class="text-center py-8 grey--text"
                >
                    No timeline data available
                </div>
                <v-timeline v-else dense class="pr-6" align-top>
                    <div v-for="(item, index) in timelineItems" :key="index">
                        <v-timeline-item
                            small
                            :color="statusColors[item.status]?.color || 'grey'"
                        >
                            <!-- Timeline content -->
                            <v-row justify="space-between">
                                <v-col cols="6">
                                    <span
                                        class="text-capitalize font-weight-bold"
                                    >
                                        {{ item.status }}
                                    </span>
                                    <p>
                                        {{ getUser(item.sendBy) || 'Unknown' }}
                                    </p>
                                </v-col>
                                <v-col class="text-right" cols="6">
                                    {{ getFormattedDate(item?.date) }}
                                </v-col>
                            </v-row>
                        </v-timeline-item>
                    </div>
                </v-timeline>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import moment from 'moment'
export default {
    name: 'TimeLinePurchase',
    props: {
        request: {
            type: Object,
            default: () => ({}),
            required: true,
        },
        users: {
            type: Array,
            default: () => [],
            required: true,
        },
    },
    data: () => ({
        statusColors: Object.freeze({
            inProgress: {
                text: 'inProgress',
                color: 'primary',
            },
            onHold: {
                text: 'onHold',
                color: 'green',
            },
            pricing: {
                text: 'pricing',
                color: '#3293e6',
            },
            preApproval: {
                text: 'preApproval',
                color: '#FF9800',
            },
            approval: {
                text: 'approval',
                color: '#27BC4F',
            },
            purchasing: {
                text: 'purchasing',
                color: 'primary',
            },
            inTransit: {
                text: 'inTransit',
                color: '#ff7800',
            },
            received: {
                text: 'received',
                color: '#00a135',
            },
            readyToClose: {
                text: 'readyToClose',
                color: 'grey',
            },
            closed: {
                text: 'closed',
                color: '#585454',
            },
            rejected: {
                text: 'rejected',
                color: 'red',
            },
        }),
        height: 0,
    }),
    computed: {
        timelineItems() {
            const stored = this.parseTimeline(
                this.request && this.request.timeline
            )
            if (stored.length > 0) {
                return stored
            }
            return this.formatTimeline(this.request || {})
        },
    },
    async mounted() {
        await this.$nextTick()
        this.onResize()
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        parseTimeline(timeline) {
            if (!timeline) return []
            let value = timeline
            if (typeof value === 'string') {
                try {
                    value = JSON.parse(value)
                    if (typeof value === 'string') {
                        value = JSON.parse(value)
                    }
                } catch (error) {
                    return []
                }
            }
            if (Array.isArray(value)) return value.filter(Boolean)
            return []
        },
        resolveUserId(value) {
            if (value == null || value === '') return null
            if (typeof value === 'string') {
                const trimmed = value.trim()
                if (trimmed.startsWith('{')) {
                    try {
                        const parsed = JSON.parse(trimmed)
                        return parsed.id || parsed
                    } catch (error) {
                        return trimmed
                    }
                }
                return trimmed
            }
            if (typeof value === 'object') {
                if (value.id && typeof value.id === 'object') {
                    return value.id.id || null
                }
                return value.id || value.uid || null
            }
            return null
        },
        resolveDate(dateValue) {
            if (!dateValue) return null
            if (typeof dateValue === 'string') {
                try {
                    const parsed = JSON.parse(dateValue)
                    if (parsed && typeof parsed === 'object') {
                        dateValue = parsed
                    }
                } catch (error) {
                    // keep original string
                }
            }
            const seconds = dateValue?._seconds || dateValue?.seconds
            if (seconds != null && seconds !== '') {
                const date = moment.unix(Number(seconds)).toDate()
                if (moment(date).isValid()) return date
            }
            if (typeof dateValue?.toDate === 'function') {
                const date = dateValue.toDate()
                if (moment(date).isValid()) return date
            }
            if (dateValue instanceof Date && moment(dateValue).isValid()) {
                return dateValue
            }
            const date = moment(dateValue).toDate()
            return moment(date).isValid() ? date : null
        },
        formatTimeline(purchase) {
            const timeline = []

            const statusDateMapping = [
                {
                    status: 'inProgress',
                    dateField: 'createdOn',
                    byField: 'createdBy',
                },
                {
                    status: 'rejected',
                    dateField: 'rejectedDate',
                    byField: 'rejectedBy',
                },
                {
                    status: 'onHold',
                    dateField: 'onHoldDate',
                    byField: 'onHoldBy',
                },
                {
                    status: 'pricing',
                    dateField: 'inProgressDate',
                    byField: 'buyer',
                },
                {
                    status: 'preApproval',
                    dateField: 'pricingDate',
                    byField: 'pricingBy',
                },
                {
                    status: 'approval',
                    dateField: 'preApprovedDate',
                    byField: 'preApprovedBy',
                },
                {
                    status: 'purchasing',
                    dateField: 'approvedDate',
                    byField: 'approvedBy',
                },
                {
                    status: 'inTransit',
                    dateField: 'purchasedDate',
                    byField: 'purchasedBy',
                },
                {
                    status: 'received',
                    dateField: 'inTransitDate',
                    byField: 'inTransitBy',
                },
                {
                    status: 'closed',
                    dateField: 'closedDate',
                    byField: 'receivedBy',
                },
            ]

            for (const mapping of statusDateMapping) {
                let dateValue = purchase[mapping.dateField]
                let byValue = this.resolveUserId(purchase[mapping.byField])

                // Fallback to assignDate for pricing status
                if (
                    mapping.status === 'pricing' &&
                    !dateValue &&
                    purchase.assignDate
                ) {
                    dateValue = purchase.assignDate
                }

                const date = this.resolveDate(dateValue)
                if (!date) continue

                if (byValue || mapping.status === 'inProgress') {
                    timeline.push({
                        status: mapping.status,
                        date: date,
                        sendBy: byValue || null,
                    })
                }
            }

            timeline.sort((a, b) => a.date - b.date)
            return timeline
        },
        closeTimeLine() {
            this.$emit('closeDialog')
        },
        formatDate(seconds) {
            const date = moment.unix(seconds).format('YYYY-MM-DD HH:mm:ss')
            return date
        },
        getFormattedDate(dateValue) {
            const date = this.resolveDate(dateValue)
            if (!date) return ''
            return this.formatDate(moment(date).unix())
        },
        getUser(id) {
            // Handle 'System' case
            if (id === 'System') {
                return 'System'
            }
            const user = this.users.find(u => u.id == id)
            if (user) {
                return user.name
            }
            return 'Unknown'
        },
        onResize() {
            const {
                title: { clientHeight: titleHeight },
            } = this.$refs
            this.height = window.innerHeight - titleHeight - 90
        },
    },
}
</script>
